import { Button, styled } from '@payright/web-components';
import { ContractListLine } from './contract-list-item';
import { type ReactNode } from 'react';
import type { PaymentFrequency } from '@payright/types-bnpl-checkout-api';

const Section = styled.section`
  .next-button {
    margin-top: 2rem;
    margin-bottom: 1rem;
  }
`;

const List = styled.ul`
  background-color: ${(props) => props.theme.colours.secondary3};
  padding: 0 1rem;
`;

const Summary = styled.div`
  background-color: ${(props) => props.theme.colours.blue.base};
  padding: 1rem;
  font-size: 1.5rem;
  text-align: right;
  margin-top: 1rem;
  color: ${(props) => props.theme.colours.white};

  span {
    align-self: center;
    vertical-align: middle;
  }

  span.payment {
    font-size: 3.3rem;
  }
`;

const LegalNotice = styled.p`
  font-size: 0.75rem;
  color: ${(props) => props.theme.colours.grey.dark};
  margin-top: 1rem;
`;

const currency = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

type ContractFormProps = {
  accountKeepingFee: number;
  alert?: ReactNode;
  establishmentFee: number;
  onSubmit: () => void;
  loanAmount: number;
  paymentPeriod: number;
  paymentProcessingFee: number;
  repaymentAmount: number;
  repaymentFrequency: string;
  withinAvailableCredit: boolean;
  approvedLimit: number;
  availableCredit: number;
  totalCreditAmount: number;
  isLoading: boolean;
};

const frequencyToPeriod = (paymentFrequency: PaymentFrequency | string): string => {
  if (paymentFrequency === 'Weekly') {
    return 'weeks';
  } else if (paymentFrequency === 'Fortnightly') {
    return 'fortnights';
  } else if (paymentFrequency === 'Monthly') {
    return 'months';
  }
  return paymentFrequency;
};

export const ContractForm = ({
  accountKeepingFee,
  alert,
  establishmentFee,
  onSubmit: handleSubmit,
  isLoading,
  loanAmount,
  paymentPeriod,
  paymentProcessingFee,
  repaymentAmount,
  repaymentFrequency,
  withinAvailableCredit,
  approvedLimit,
  availableCredit,
  totalCreditAmount,
}: ContractFormProps) => {
  const formattedPaymentAmount = currency.format(repaymentAmount);

  return (
    <Section>
      <List>
        {withinAvailableCredit && (
          <>
            <ContractListLine
              title="Total Credit Limit of your BNPL Contract"
              value={currency.format(approvedLimit)}
            />
            <ContractListLine
              title="Available Credit Limit (before this Plan)"
              value={currency.format(availableCredit)}
            />
          </>
        )}

        <ContractListLine title="Plan Amount" value={currency.format(loanAmount)} />
        <ContractListLine title="Payment Frequency" value={repaymentFrequency} />
        <ContractListLine
          title="Payment Period"
          value={`${paymentPeriod} ${frequencyToPeriod(repaymentFrequency ?? '')}`}
        />
        <ContractListLine title="Payment Amount" value={formattedPaymentAmount} />
        <ContractListLine title="Establishment Fee" value={currency.format(establishmentFee)} />

        {withinAvailableCredit && (
          <ContractListLine
            title="Available Credit Limit (after this Plan)"
            value={currency.format(availableCredit - totalCreditAmount)}
          />
        )}
      </List>
      <Summary>
        <span>
          {paymentPeriod} {repaymentFrequency} payments of
        </span>
        <span className="payment">
          {'  '}
          {formattedPaymentAmount}
        </span>
      </Summary>
      <LegalNotice>
        * Included in the above {repaymentFrequency} payment is an account keeping fee of{' '}
        {currency.format(accountKeepingFee)} and a payment processing fee of{' '}
        {currency.format(paymentProcessingFee)}. These fees are not duplicated if you have more than
        one active Plan.
      </LegalNotice>

      <Button
        className="next-button"
        withShadow
        maxWidth="100%"
        handleClick={handleSubmit}
        disabled={isLoading}
      >
        {withinAvailableCredit
          ? 'Click here to accept and proceed with this Plan'
          : 'Review and accept your financial table and BNPL contract'}
      </Button>

      {alert}

      <LegalNotice>
        {withinAvailableCredit ? (
          <>
            Your new BNPL Plan is part of your existing Payright BNPL Contract which is a low-cost
            credit contract.
          </>
        ) : (
          <>
            Your Payright BNPL payment plan is a low-cost credit contract. Navalo Financial Services
            Group Pty Ltd (trading as Payright) has made an election to ASIC under s133BXA(1) of the
            National Consumer Credit Protection Act 2009 (Cth) to adopt a modified form of
            Responsible Lending. Payright has applied these modified responsible lending obligations
            to your payment plan when assessing whether this payment plan is suitable for you.
          </>
        )}
      </LegalNotice>
    </Section>
  );
};
